import { useCallback, useMemo, useRef } from "react";
import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { FeatureCollection, Geometry, Point } from "geojson";
import { featureCollection, point } from "@turf/helpers";
import GeographyService from "@app/geography/geographyService";
import ZipProjectService from "@app/projects/zipProjectService";
import { MemberMapResults, ProviderMapResults } from "@app/projects/results/common/mapping";
import { CountyModel, ZipCodeModel } from "@app/projects/network-assessment/maps/network-coverage-map/interfaces";
import { ProviderActionStatusEnum } from "@app/projects/enumerators";
import { ZipCoverageMapSelections, ZipProviderFilters } from "@app/projects/zip-projects/maps/interfaces";
import { DEFAULT_ZIP_PROVIDER_FILTERS, filterZipProviders } from "@app/projects/zip-projects/maps/zipProviderFilters";
import { getSelectedZipCodes, isMultiZipSelection } from "@app/projects/zip-projects/maps/multiZipSelection";
import { ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";

export interface ZipCoverageMapData {
  boundaries: FeatureCollection<Geometry, ZipCodeModel> | null;
  loadingBoundaries: boolean;
  focusedZipBoundary: FeatureCollection<Geometry, ZipCodeModel> | null;
  loadingFocusedZipBoundary: boolean;
  countyBoundaries: GeoJSON.FeatureCollection<Geometry, CountyModel> | null;
  loadingCountyBoundaries: boolean;
  /** Member pins for the focused zip (202283). Always a collection — empty rather than null. */
  memberFeatures: FeatureCollection<Point, MemberMapResults>;
  loadingMembers: boolean;
  providerFeatures: FeatureCollection<Point, ProviderMapResults>;
  providers: ProviderMapResults[];
  filteredProviders: ProviderMapResults[];
  loadingProviders: boolean;
  /** Provider sources whose fetch failed (202347) — drives the panel's per-source error message. */
  providerFetchErrors: ProviderNetworkTypeEnum[];
  /** Re-fetches the provider access points, so an action taken in a popup shows up on the pins. */
  refreshProviders: () => void;
  /** Whether any fetched provider carries the matching action status. Drives the legend rows (202310). */
  hasTargetedProviders: boolean;
  hasTerminatedProviders: boolean;
  hasExcludedProviders: boolean;
  /** The same three flags after filtering. Drives the legend, so a row only shows for pins on the map. */
  visibleHasTargetedProviders: boolean;
  visibleHasTerminatedProviders: boolean;
  visibleHasExcludedProviders: boolean;
}

interface ProviderActionStatusFlags {
  excluded: boolean;
  targeted: boolean;
  terminated: boolean;
}

// One pass for all three legend flags, rather than three `some` scans.
const collectActionStatusFlags = (providers: ProviderMapResults[]): ProviderActionStatusFlags => {
  const flags: ProviderActionStatusFlags = { excluded: false, targeted: false, terminated: false };
  for (const provider of providers) {
    if (provider.scenarioModelProviderStatus === ProviderActionStatusEnum.FullyTargeted) flags.targeted = true;
    else if (provider.scenarioModelProviderStatus === ProviderActionStatusEnum.FullyTerminated) flags.terminated = true;
    else if (provider.scenarioModelProviderStatus === ProviderActionStatusEnum.FullyExcluded) flags.excluded = true;
  }
  return flags;
};

/**
 * ZIP coverage map data layer. Fetches ZIP + county boundaries (for the layer control's Boundaries toggles)
 * and, once selections are applied, the focused zip's member points (202283) and the provider access points
 * for each enabled Provider Source (202310). Adequacy remains an additive slot (TODO below).
 *
 * SOURCE (extracted, 199592): network-coverage-map/contexts/NetworkCoverageMapDataContext.tsx
 * (the boundary fetch). ZIP-owned (county context untouched).
 * CONSOLIDATION (199592): boundary fetch → shared `useMapBoundaries`; other fetches → shared
 * hooks + reused county layers (see TODOs). Data/context stay ZIP-owned; no shared/generic context.
 */
export const useZipCoverageMapData = (
  regionSetId: number | null,
  zipCode: string | null,
  projectId: number | null = null,
  selections: ZipCoverageMapSelections | null = null,
  filters: ZipProviderFilters = DEFAULT_ZIP_PROVIDER_FILTERS,
): ZipCoverageMapData => {
  const geographyService = useRef(new GeographyService()).current;
  const zipProjectService = useRef(new ZipProjectService()).current;
  const queryClient = useQueryClient();

  const refreshProviders = useCallback(
    () => { void queryClient.invalidateQueries({ queryKey: ["zip-coverage-map", "access-points"] }); },
    [queryClient],
  );

  // Both members and providers are keyed off the applied run configuration and specialty — pulled out of
  // `selections` rather than threaded as separate params, since the two features need the same values.
  const projectRunConfigurationId = selections?.resultsAsOfId ?? null;
  const specialtyGroupCode = selections?.specialtyGroupCode.key ?? null;

  // With more than one zip, only the selected zips' boundaries are fetched; the county-wide, member
  // and provider reads stay single-zip until future work items. Disabled queries still return cached
  // data, so the results below are overridden too, not just `enabled`.
  const isMultiZip = isMultiZipSelection(selections);
  const selectedZipCodes = useMemo(() => getSelectedZipCodes(selections).slice().sort(), [selections]);

  // react-query `isError` is not surfaced — a failed boundary fetch just won't zoom (no message).
  // Follow-up (ticket 199592): surface a fetch-error toast (county try/catches + falls back to empty).
  const { data: boundaries, isFetching: loadingBoundaries } = useQuery({
    enabled: regionSetId !== null && !isMultiZip,
    // No AbortSignal (matches county usage); threading one is deferred to the dedup task.
    queryFn: () => geographyService.getZipCodeBordersByRegionSetId(String(regionSetId)),
    queryKey: ["zip-coverage-map", "boundaries", regionSetId],
    staleTime: 5 * 60 * 1000,
  });

  const { data: focusedZipBoundary, isFetching: loadingFocusedZipBoundary } = useQuery({
    enabled: zipCode !== null && !isMultiZip,
    queryFn: () => geographyService.getZipCodeBorderByZipCode(zipCode!),
    queryKey: ["zip-coverage-map", "focused-zip-boundary", zipCode],
    staleTime: 5 * 60 * 1000,
  });

  const { data: selectedZipBoundaries, isFetching: loadingSelectedZipBoundaries } = useQuery({
    enabled: isMultiZip,
    queryFn: () => geographyService.getZipCodeBordersByZipCodes(selectedZipCodes),
    queryKey: ["zip-coverage-map", "selected-zip-boundaries", selectedZipCodes],
    staleTime: 5 * 60 * 1000,
  });

  const { data: countyBoundaries, isFetching: loadingCountyBoundaries } = useQuery({
    enabled: regionSetId !== null && !isMultiZip,
    queryFn: () => geographyService.getCountyBordersByRegionSetId(String(regionSetId)),
    queryKey: ["zip-coverage-map", "county-boundaries", regionSetId],
    staleTime: 5 * 60 * 1000,
  });

  const { data: memberResults, isFetching: loadingMembers } = useQuery({
    enabled:
      !isMultiZip &&
      projectId !== null &&
      projectRunConfigurationId !== null &&
      !!zipCode &&
      !!specialtyGroupCode,
    queryFn: () => zipProjectService.GetZipMemberResultsAsync(projectId!, projectRunConfigurationId!, zipCode!, specialtyGroupCode!),
    queryKey: ["zip-coverage-map", "members", projectId, projectRunConfigurationId, zipCode, specialtyGroupCode],
    staleTime: 5 * 60 * 1000,
  });

  const memberFeatures = useMemo(
    (): FeatureCollection<Point, MemberMapResults> =>
      featureCollection((isMultiZip ? [] : memberResults ?? []).map((member) => point([member.lon, member.lat], member))),
    [isMultiZip, memberResults],
  );

  // One query per enabled Provider Source, so a failing source degrades to "no pins for that source" rather
  // than blanking the map. Guard the array, not `enabled` — the latter sits inside each query object and so
  // cannot protect this map() from a null `selections`.
  const providerSources = isMultiZip ? [] : selections?.providerSources ?? [];
  const providerQueries = useQueries({
    queries: providerSources.map((source) => ({
      enabled: projectId !== null,
      queryFn: ({ signal }: { signal: AbortSignal }) => zipProjectService.servicingAccessPoints(
        projectId!,
        selections!.resultsAsOfId,
        selections!.serviceArea.zipCode,
        selections!.specialtyGroupCode.key,
        source,
        signal,
      ),
      queryKey: [
        "zip-coverage-map",
        "access-points",
        projectId,
        selections!.resultsAsOfId,
        selections!.serviceArea.zipCode,
        selections!.specialtyGroupCode.key,
        source,
      ],
      staleTime: 5 * 60 * 1000,
    })),
  });

  const loadingProviders = providerQueries.some((query) => query.isFetching);

  // Query order mirrors selections.providerSources (see the useQueries call above), so index back to source.
  const providerFetchErrors = providerSources.filter(
    (_, index) => providerQueries[index]?.isError,
  );

  // `useQueries` returns a fresh array each render, so memoise on when each query's data last changed.
  const providerDataKey = providerQueries.map((query) => query.dataUpdatedAt).join(",");

  // `accessPoints` is optional and a failed source yields undefined data, so flatten defensively. The API can
  // return one providerId several times within a source, differing only by specialtyCode, so specialtyCode has to
  // be part of providerUniqueId for it to stay unique — we do not de-duplicate here.
  const { providers, statuses } = useMemo(() => {
    const fetched = providerQueries.flatMap((query) => (query.data?.accessPoints ?? []).map((accessPoint) => ({
      ...accessPoint,
      providerUniqueId: `${accessPoint.providerNetworkType}-${accessPoint.providerId}-${accessPoint.specialtyCode}`,
    })) as ProviderMapResults[]);

    return { providers: fetched, statuses: collectActionStatusFlags(fetched) };
  }, [providerDataKey]);

  const { filteredProviders, providerFeatures, visibleStatuses } = useMemo(() => {
    const visible = filterZipProviders(providers, filters);

    return {
      filteredProviders: visible,
      providerFeatures: featureCollection(visible.map((p) => point([p.longitude, p.latitude], p))),
      visibleStatuses: collectActionStatusFlags(visible),
    };
  }, [providers, filters]);

  // TODO(199592 adequacy): fetch ZIP aggregates → region fill + Map Details Box (MapDetailsBox is prop-driven, reusable).

  return {
    boundaries: (isMultiZip ? selectedZipBoundaries : boundaries) ?? null,
    countyBoundaries: isMultiZip ? null : countyBoundaries ?? null,
    filteredProviders,
    focusedZipBoundary: (isMultiZip ? selectedZipBoundaries : focusedZipBoundary) ?? null,
    hasExcludedProviders: statuses.excluded,
    hasTargetedProviders: statuses.targeted,
    hasTerminatedProviders: statuses.terminated,
    loadingBoundaries,
    loadingCountyBoundaries,
    loadingFocusedZipBoundary: loadingFocusedZipBoundary || loadingSelectedZipBoundaries,
    loadingMembers,
    loadingProviders,
    memberFeatures,
    providerFeatures,
    providerFetchErrors,
    providers,
    refreshProviders,
    visibleHasExcludedProviders: visibleStatuses.excluded,
    visibleHasTargetedProviders: visibleStatuses.targeted,
    visibleHasTerminatedProviders: visibleStatuses.terminated,
  };
};
